import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Check, X, Warning, Plus, Trash, Sparkle, UploadSimple, PencilSimple } from '@phosphor-icons/react';
import { useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const ExpenseMapper = () => {
  const [searchParams] = useSearchParams();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const fileInputRef = useRef(null);
  const [grants, setGrants] = useState([]);
  const [newExpense, setNewExpense] = useState({
    date: new Date().toISOString().split('T')[0],
    vendor: '',
    amount: '',
    category: '',
    grant_id: '',
    description: ''
  });

  const fetchExpenses = useCallback(async (pageNum = 1, isLoadMore = false) => {
    if (!isLoadMore) setLoading(true);
    try {
      const response = await axios.get(`${API}/expenses`, {
        params: {
          page: pageNum,
          limit: 15,
          status: filter !== 'all' && filter !== 'flagged' ? filter : undefined
        }
      });
      
      const { expenses: newExps, total, totalPages } = response.data;
      
      if (isLoadMore) {
        setExpenses(prev => [...prev, ...newExps]);
      } else {
        setExpenses(newExps);
      }
      
      setTotalExpenses(total);
      setHasMore(pageNum < totalPages);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchGrants = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/grants`);
      setGrants(response.data.grants || []);
    } catch (error) {
      console.error('Failed to fetch grants:', error);
    }
  }, []);

  useEffect(() => {
    fetchExpenses(1, false);
    fetchGrants();
    if (searchParams.get('new') === 'true') {
      setShowModal(true);
    }
  }, [fetchExpenses, fetchGrants, searchParams]);

  useEffect(() => {
    fetchExpenses(1, false);
  }, [fetchExpenses]);

  const loadMore = () => {
    if (hasMore && !loading) {
      fetchExpenses(page + 1, true);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setExtracting(true);
    setShowModal(true);
    setIsEditing(false);

    try {
      const response = await axios.post(`${API}/extract/expense`, {
        text_content: `Invoice for ${file.name}. Mocked data extraction.`
      });

      setNewExpense({
        ...newExpense,
        ...response.data,
        amount: response.data.amount.toString()
      });
    } catch (error) {
      console.error('Extraction failed:', error);
      alert('AI Extraction failed. Please enter details manually.');
    } finally {
      setExtracting(false);
    }
  };

  const approveExpense = async (expenseId, action) => {
    try {
      await axios.post(`${API}/expenses/${expenseId}/approve?action=${action}`);
      fetchExpenses(1, false);
    } catch (error) {
      console.error('Failed to approve expense:', error);
    }
  };

  const handleEditExpense = (expense) => {
    setNewExpense({
      date: new Date(expense.date).toISOString().split('T')[0],
      vendor: expense.vendor,
      amount: expense.amount.toString(),
      category: expense.category,
      grant_id: expense.grant_id || '',
      description: expense.description || ''
    });
    setEditingId(expense.id);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleSubmitExpense = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await axios.put(`${API}/expenses/${editingId}`, {
          ...newExpense,
          amount: parseFloat(newExpense.amount)
        });
      } else {
        await axios.post(`${API}/expenses`, {
          ...newExpense,
          amount: parseFloat(newExpense.amount)
        });
      }
      
      setShowModal(false);
      setIsEditing(false);
      setEditingId(null);
      setNewExpense({
        date: new Date().toISOString().split('T')[0],
        vendor: '',
        amount: '',
        category: '',
        grant_id: '',
        description: ''
      });
      fetchExpenses(1, false);
    } catch (error) {
      console.error('Failed to save expense:', error);
      alert('Failed to save expense');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-[#22c55e]/15 text-[#22c55e]';
      case 'pending': return 'bg-[#f59e0b]/15 text-[#f59e0b]';
      case 'rejected': return 'bg-[#ef4444]/15 text-[#ef4444]';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const displayedExpenses = filter === 'flagged' 
    ? expenses.filter(exp => exp.flags && exp.flags.length > 0)
    : expenses;

  return (
    <div className="h-full overflow-auto p-4 sm:p-6 space-y-6 bg-background text-foreground transition-colors duration-300">
      <div className="border-b border-border pb-4 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
        <div>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-2">Expense Mapper</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Track and approve expenses across grants</p>
        </div>
        <div className="flex flex-wrap gap-3 w-full sm:w-auto">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
            accept="image/*,application/pdf"
          />
          <button
            onClick={() => fileInputRef.current.click()}
            className="flex-1 sm:flex-none px-4 py-2 bg-card border border-[#E04E39]/30 text-[#E04E39] rounded-md flex items-center justify-center gap-2 hover:bg-[#E04E39]/10 transition-colors font-medium text-sm"
          >
            <Sparkle size={20} weight="fill" />
            AI Extract
          </button>
          <button
            onClick={() => {
              setIsEditing(false);
              setShowModal(true);
            }}
            className="flex-1 sm:flex-none px-4 py-2 bg-[#E04E39] hover:bg-[#E86B5A] text-white rounded-md flex items-center justify-center gap-2 transition-colors font-medium text-sm shadow-md"
          >
            <Plus size={20} weight="bold" />
            New
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 pb-2">
        {['all', 'pending', 'approved', 'flagged'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-md text-xs font-bold capitalize transition-all ${
              filter === f ? 'bg-[#E04E39] text-white shadow-md' : 'bg-card text-muted-foreground hover:text-foreground border border-border'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Date</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Vendor</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Category</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Grant</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Flags</th>
                <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedExpenses.map((expense, idx) => (
                <tr 
                  key={expense.id}
                  className={`border-b border-border hover:bg-muted/30 transition-colors ${idx % 2 === 0 ? 'bg-card' : 'bg-muted/10'}`}
                >
                  <td className="px-6 py-4 text-sm text-muted-foreground whitespace-nowrap">{new Date(expense.date).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-sm font-medium">{expense.vendor}</td>
                  <td className="px-6 py-4 text-sm font-bold">${expense.amount.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground font-medium italic">{expense.category}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {expense.grant_name || <span className="text-muted-foreground/30 italic">Unmapped</span>}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-[10px] font-bold rounded uppercase tracking-wider ${getStatusColor(expense.status)}`}>
                      {expense.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {expense.flags?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {expense.flags.map((flag, idx) => (
                          <div key={idx} className="flex items-center gap-1 px-1.5 py-0.5 bg-[#ef4444]/10 border border-[#ef4444]/30 rounded">
                            <Warning size={10} className="text-[#ef4444]" />
                            <span className="text-[10px] text-[#ef4444] font-bold uppercase">{flag}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleEditExpense(expense)}
                        className="p-2 bg-muted text-muted-foreground rounded hover:bg-muted/80 transition-colors"
                        title="Edit"
                      >
                        <PencilSimple size={16} />
                      </button>
                      {expense.status === 'pending' && (
                        <>
                          <button
                            onClick={() => approveExpense(expense.id, 'approve')}
                            className="p-2 bg-[#22c55e]/15 text-[#22c55e] rounded hover:bg-[#22c55e]/25 transition-colors"
                          >
                            <Check size={16} weight="bold" />
                          </button>
                          <button
                            onClick={() => approveExpense(expense.id, 'reject')}
                            className="p-2 bg-[#ef4444]/15 text-[#ef4444] rounded hover:bg-[#ef4444]/25 transition-colors"
                          >
                            <X size={16} weight="bold" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <div className="p-4 flex justify-center bg-muted/20">
            <button
              onClick={loadMore}
              disabled={loading}
              className="text-sm text-muted-foreground hover:text-foreground font-bold tracking-tight uppercase"
            >
              {loading ? 'Processing...' : 'Load More Records'}
            </button>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-md w-full max-w-lg shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-border flex justify-between items-center bg-muted/50">
              <div className="flex items-center gap-3">
                <h2 className="text-lg sm:text-xl font-bold">
                  {extracting ? 'AI Analysis...' : isEditing ? 'Edit Transaction' : 'New Transaction'}
                </h2>
                {extracting && <Sparkle size={20} weight="fill" className="text-[#E04E39] animate-spin" />}
              </div>
              <button onClick={() => setShowModal(false)} className="text-muted-foreground hover:text-foreground">
                <X size={24} />
              </button>
            </div>
            
            <div className="overflow-y-auto flex-1 p-4 sm:p-6">
              {extracting ? (
                <div className="py-12 flex flex-col items-center justify-center space-y-4">
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-[#E04E39] animate-[shimmer_2s_infinite_linear] w-1/2" style={{ background: 'linear-gradient(90deg, transparent, #E04E39, transparent)' }}></div>
                  </div>
                  <p className="text-muted-foreground animate-pulse text-sm">VANCO AI is digitizing the invoice details...</p>
                </div>
              ) : (
                <form onSubmit={handleSubmitExpense} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Date</label>
                      <input
                        type="date"
                        required
                        value={newExpense.date}
                        onChange={(e) => setNewExpense({...newExpense, date: e.target.value})}
                        className="w-full px-4 py-2 bg-background border border-border rounded text-foreground focus:border-[#E04E39] outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Amount ($)</label>
                      <input
                        type="number"
                        required
                        placeholder="0.00"
                        value={newExpense.amount}
                        onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})}
                        className="w-full px-4 py-2 bg-background border border-border rounded text-foreground focus:border-[#E04E39] outline-none text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Vendor / Payee</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g., Apple Store, Office Depot"
                      value={newExpense.vendor}
                      onChange={(e) => setNewExpense({...newExpense, vendor: e.target.value})}
                      className="w-full px-4 py-2 bg-background border border-border rounded text-foreground focus:border-[#E04E39] outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Category</label>
                    <select
                      required
                      value={newExpense.category}
                      onChange={(e) => setNewExpense({...newExpense, category: e.target.value})}
                      className="w-full px-4 py-2 bg-background border border-border rounded text-foreground focus:border-[#E04E39] outline-none text-sm"
                    >
                      <option value="">Select Category</option>
                      <option value="Supplies">Supplies</option>
                      <option value="Travel">Travel</option>
                      <option value="Services">Professional Services</option>
                      <option value="Events">Events</option>
                      <option value="Utilities">Utilities</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Grant Mapping</label>
                    <select
                      value={newExpense.grant_id}
                      onChange={(e) => setNewExpense({...newExpense, grant_id: e.target.value})}
                      className="w-full px-4 py-2 bg-background border border-border rounded text-foreground focus:border-[#E04E39] outline-none text-sm"
                    >
                      <option value="">Unmapped (Internal)</option>
                      {grants.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="pt-4 flex flex-col sm:flex-row gap-3 border-t border-border">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="order-2 sm:order-1 flex-1 px-4 py-3 bg-transparent border border-border text-foreground rounded hover:bg-muted font-medium transition-all text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="order-1 sm:order-2 flex-1 px-4 py-3 bg-[#E04E39] hover:bg-[#E86B5A] text-white rounded font-bold shadow-lg transition-all text-sm"
                    >
                      {isEditing ? 'UPDATE' : 'SUBMIT'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseMapper;
